/**
 * ProcessingGuard - Monitors and prevents infinite loops in discipline processing
 *
 * This utility provides safeguards against:
 * - Infinite loops in mention/consequence creation
 * - Excessive Firebase reads
 * - Circular rule processing
 *
 * IMPORTANT: This only logs warnings and metrics - it does NOT block operations
 * to avoid breaking existing functionality.
 */

import { disciplineLogger } from '../../../utils/disciplineLogger';

class ProcessingGuard {
  constructor() {
    // Track mentions being processed to detect loops
    this.activeMentionProcessing = new Map(); // mentionId -> { startTime, count }

    // Track consequences being processed
    this.activeConsequenceProcessing = new Map(); // key -> { startTime, count }

    // Track read operations per session with context
    this.sessionReads = new Map(); // operation -> count
    this.sessionStartTime = Date.now();
    this.currentContext = null; // Current operation context for error messages

    // Configuration - Progressive thresholds (First, do no harm: warnings only until critical)
    this.config = {
      maxMentionReprocessing: 3, // Warn if same mention processed 3+ times
      maxConsequenceChain: 10, // Warn if consequence chain exceeds 10
      maxProcessingTimeMs: 30000, // Warn if processing takes > 30 seconds
      sessionResetMs: 60000, // Reset session counters after 1 minute

      // Progressive read thresholds
      warnAtReads: 50,        // First warning - might be normal for complex operations
      alertAtReads: 100,      // Second warning - unusual but not critical yet
      criticalAtReads: 250,   // Critical warning - likely a problem
      circuitBreakerReads: 500, // STOP - definitely a loop (reduced from 10,000)
    };

    // Metrics for monitoring
    this.metrics = {
      loopsDetected: 0,
      excessiveReadsWarnings: 0,
      slowProcessingWarnings: 0,
      circuitBreakerTriggered: 0,
    };

    // Error context for detailed reporting
    this.lastError = null;
  }

  /**
   * Check if mention processing should continue (always returns true, only logs warnings)
   * @param {string} mentionId - Mention being processed
   * @param {string} operation - Operation being performed
   * @returns {boolean} Always true - never blocks processing
   */
  checkMentionProcessing(mentionId, operation = 'process') {
    const key = `${mentionId}:${operation}`;
    const now = Date.now();

    if (this.activeMentionProcessing.has(key)) {
      const processing = this.activeMentionProcessing.get(key);
      processing.count++;

      // Check for potential loop
      if (processing.count >= this.config.maxMentionReprocessing) {
        this.metrics.loopsDetected++;
        disciplineLogger.critical('⚠️ POTENTIAL LOOP DETECTED', {
          mentionId,
          operation,
          reprocessCount: processing.count,
          duration: now - processing.startTime,
          warning: 'Mention being reprocessed multiple times'
        }, 'guard');

        // Log to console for visibility
        console.warn(`[ProcessingGuard] Potential loop: Mention ${mentionId} processed ${processing.count} times`);
      }

      // Check for slow processing
      if (now - processing.startTime > this.config.maxProcessingTimeMs) {
        this.metrics.slowProcessingWarnings++;
        disciplineLogger.warn('⚠️ SLOW PROCESSING DETECTED', {
          mentionId,
          operation,
          duration: now - processing.startTime,
          threshold: this.config.maxProcessingTimeMs
        }, 'guard');
      }
    } else {
      this.activeMentionProcessing.set(key, { startTime: now, count: 1 });
    }

    // Always return true - never block
    return true;
  }

  /**
   * Mark mention processing as complete
   * @param {string} mentionId - Mention ID
   * @param {string} operation - Operation that completed
   */
  completeMentionProcessing(mentionId, operation = 'process') {
    const key = `${mentionId}:${operation}`;
    const processing = this.activeMentionProcessing.get(key);

    if (processing && processing.count > 1) {
      disciplineLogger.info('Mention processing completed after multiple attempts', {
        mentionId,
        operation,
        attempts: processing.count,
        duration: Date.now() - processing.startTime
      }, 'guard');
    }

    this.activeMentionProcessing.delete(key);
  }

  /**
   * Check consequence processing chain depth
   * @param {string} studentId - Student ID
   * @param {string} consequenceType - Type of consequence
   * @param {number} chainDepth - Current chain depth
   * @returns {boolean} Always true - never blocks processing
   */
  checkConsequenceChain(studentId, consequenceType, chainDepth = 0) {
    const key = `${studentId}:${consequenceType}`;

    if (chainDepth >= this.config.maxConsequenceChain) {
      disciplineLogger.critical('⚠️ DEEP CONSEQUENCE CHAIN DETECTED', {
        studentId,
        consequenceType,
        chainDepth,
        threshold: this.config.maxConsequenceChain,
        warning: 'Consequence chain may be circular'
      }, 'guard');

      console.warn(`[ProcessingGuard] Deep chain: ${chainDepth} consequences for student ${studentId}`);
    }

    return true;
  }

  /**
   * Set current operation context for detailed error reporting
   * @param {Object} context - Context information
   */
  setContext(context) {
    this.currentContext = context;
  }

  /**
   * Clear current operation context
   */
  clearContext() {
    this.currentContext = null;
  }

  /**
   * Track and monitor read operations with progressive warnings
   * @param {string} operation - Operation name
   * @param {number} readCount - Number of reads
   * @param {Object} context - Optional context for error reporting
   */
  trackReads(operation, readCount = 1, context = {}) {
    const now = Date.now();

    // Reset session if needed
    if (now - this.sessionStartTime > this.config.sessionResetMs) {
      this.sessionReads.clear();
      this.sessionStartTime = now;
    }

    // Track reads
    const current = this.sessionReads.get(operation) || 0;
    this.sessionReads.set(operation, current + readCount);

    // Calculate total reads in session
    let totalReads = 0;
    for (const count of this.sessionReads.values()) {
      totalReads += count;
    }

    const sessionDuration = Math.max((now - this.sessionStartTime) / 1000, 1);

    // PROGRESSIVE WARNINGS - First, do no harm: only warn, don't break until critical

    // ⚠️ LEVEL 1: First warning at 50 reads
    if (totalReads === this.config.warnAtReads) {
      disciplineLogger.info('⚠️ MODERATE READ COUNT', {
        operation,
        totalReads,
        threshold: this.config.warnAtReads,
        topOperations: this.getTopOperations(3),
        context: this.currentContext || context,
        message: 'Read count is higher than expected but may be normal for complex operations'
      }, 'guard');
    }

    // 🟠 LEVEL 2: Alert at 100 reads
    if (totalReads === this.config.alertAtReads) {
      this.metrics.excessiveReadsWarnings++;
      disciplineLogger.warn('🟠 HIGH READ COUNT DETECTED', {
        operation,
        totalReads,
        threshold: this.config.alertAtReads,
        sessionDuration: `${sessionDuration.toFixed(1)}s`,
        topOperations: this.getTopOperations(5),
        context: this.currentContext || context,
        message: 'Read count is unusually high - check for inefficient queries or loops'
      }, 'guard');

      console.warn(`[ProcessingGuard] High read count: ${totalReads} reads in ${sessionDuration.toFixed(1)}s`);
    }

    // 🔴 LEVEL 3: Critical at 250 reads
    if (totalReads === this.config.criticalAtReads) {
      this.metrics.excessiveReadsWarnings++;
      disciplineLogger.critical('🔴 CRITICAL READ COUNT', {
        operation,
        totalReads,
        threshold: this.config.criticalAtReads,
        sessionDuration: `${sessionDuration.toFixed(1)}s`,
        topOperations: this.getTopOperations(10),
        context: this.currentContext || context,
        warning: 'Very high read count - likely a loop or misconfigured rule',
        message: 'System will stop processing at 500 reads to prevent damage'
      }, 'guard');

      console.error(`[ProcessingGuard] CRITICAL: ${totalReads} reads - likely loop detected`);
    }

    // 🚨 LEVEL 4: CIRCUIT BREAKER at 500 reads
    if (totalReads >= this.config.circuitBreakerReads) {
      this.metrics.circuitBreakerTriggered++;

      const errorDetails = {
        totalReads,
        threshold: this.config.circuitBreakerReads,
        sessionDuration: `${sessionDuration.toFixed(1)}s`,
        topOperations: this.getTopOperations(10),
        context: this.currentContext || context,
        timestamp: new Date().toISOString()
      };

      // Store for error reporting
      this.lastError = errorDetails;

      disciplineLogger.critical('🚨 CIRCUIT BREAKER TRIGGERED', errorDetails, 'guard');

      console.error('[ProcessingGuard] CIRCUIT BREAKER TRIGGERED:', errorDetails);

      // Create detailed error message
      const topOp = this.getTopOperations(1)[0];
      const errorMessage = `CIRCUIT BREAKER: Stopped processing after ${totalReads} reads to prevent infinite loop. ` +
        `Top operation: ${topOp?.operation || 'unknown'} (${topOp?.reads || 0} reads). ` +
        `This usually indicates a misconfigured rule or circular dependency.`;

      const error = new Error(errorMessage);
      error.code = 'CIRCUIT_BREAKER';
      error.details = errorDetails;

      throw error;
    }
  }

  /**
   * Get top operations by read count
   * @param {number} limit - Number of top operations to return
   * @returns {Array} Top operations
   */
  getTopOperations(limit = 5) {
    const sorted = Array.from(this.sessionReads.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit);

    return sorted.map(([op, count]) => ({ operation: op, reads: count }));
  }

  /**
   * Get current metrics
   * @returns {Object} Current metrics
   */
  getMetrics() {
    const now = Date.now();
    const sessionDuration = (now - this.sessionStartTime) / 1000;

    let totalReads = 0;
    for (const count of this.sessionReads.values()) {
      totalReads += count;
    }

    return {
      ...this.metrics,
      activeMentions: this.activeMentionProcessing.size,
      activeConsequences: this.activeConsequenceProcessing.size,
      sessionReads: totalReads,
      sessionDuration,
      readsPerMinute: Math.round((totalReads / sessionDuration) * 60),
      topOperations: this.getTopOperations(3),
      currentContext: this.currentContext
    };
  }

  /**
   * Get last error details for user-friendly error messages
   * @returns {Object|null} Last error details or null
   */
  getLastError() {
    return this.lastError;
  }

  /**
   * Get user-friendly error summary
   * @returns {Object} Error summary for UI display
   */
  getUserFriendlyError() {
    if (!this.lastError) {
      return null;
    }

    const topOp = this.lastError.topOperations[0];
    const service = topOp?.operation?.split('.')[0] || 'Unknown service';

    return {
      title: 'System Overload Detected',
      message: `Processing stopped after ${this.lastError.totalReads} database operations to prevent system damage.`,
      details: `The issue occurred in ${service}. This usually indicates a misconfigured rule or circular dependency.`,
      errorCode: `LOOP_${this.lastError.totalReads}`,
      technicalDetails: {
        topOffender: topOp?.operation,
        offenderReads: topOp?.reads,
        duration: this.lastError.sessionDuration,
        context: this.lastError.context
      }
    };
  }

  /**
   * Reset all counters (useful for testing)
   */
  reset() {
    this.activeMentionProcessing.clear();
    this.activeConsequenceProcessing.clear();
    this.sessionReads.clear();
    this.sessionStartTime = Date.now();
    this.currentContext = null;
    this.lastError = null;
    this.metrics = {
      loopsDetected: 0,
      excessiveReadsWarnings: 0,
      slowProcessingWarnings: 0,
      circuitBreakerTriggered: 0,
    };

    disciplineLogger.info('ProcessingGuard reset', null, 'guard');
  }
}

// Export singleton instance
export const processingGuard = new ProcessingGuard();

// Export class for testing
export { ProcessingGuard };